import { NGRAM_ROLLING_WINDOW_SIZE } from "@/constants";

interface WordDiff {
  deletions: { index: number; value: string[] }[];
  insertions: { index: number; value: string[] }[];
}

/**
 * Computes the word-level differences between two text strings using a dynamic programming approach
 * based on the Longest Common Subsequence (LCS) algorithm.
 *
 * @param sentence - The original text string to compare from
 * @param replaceWith - The new text string to compare to
 * @returns WordDiff object containing all deletions and insertions
 */
export function getWordDiff(sentence: string, replaceWith: string): WordDiff {
  // Split both sentences into arrays of words using whitespace as delimiter
  const oldWords = sentence.split(/\s+/);
  const newWords = replaceWith.split(/\s+/);

  // Initialize arrays to store deletions and insertions
  const deletions = [];
  const insertions = [];

  // Build LCS (Longest Common Subsequence) table
  // This is a dynamic programming approach to find the longest sequence of words that appear
  // in both strings in the same order (though not necessarily consecutively)
  // lcs[i][j] stores the length of the LCS of oldWords[0...i-1] and newWords[0...j-1]
  const lcs = Array(oldWords.length + 1)
    .fill(null)
    .map(() => Array(newWords.length + 1).fill(0));

  // Populate the LCS table from bottom-right to top-left
  // This reverse direction helps with the diff calculation later
  for (let i = oldWords.length - 1; i >= 0; i--) {
    for (let j = newWords.length - 1; j >= 0; j--) {
      // If words match, increment LCS length by 1 from the diagonal cell
      if (oldWords[i] === newWords[j]) {
        lcs[i][j] = lcs[i + 1][j + 1] + 1;
      } else {
        // Otherwise, take the max LCS length from either the cell below or the cell to the right
        // This represents either skipping a word in the old text or the new text
        lcs[i][j] = Math.max(lcs[i + 1][j], lcs[i][j + 1]);
      }
    }
  }

  // Use the LCS table to find the differences (deletions and insertions)
  // Start from the top-left of the table (position 0,0)
  let i = 0; // Index for oldWords
  let j = 0; // Index for newWords

  // Traverse both word arrays until we've processed all words
  while (i < oldWords.length || j < newWords.length) {
    // If words match, they're part of the LCS, so move to the next words in both sentences
    if (i < oldWords.length && j < newWords.length && oldWords[i] === newWords[j]) {
      i++;
      j++;
    } else {
      // Words don't match - we need to identify what changed

      // ---- Process deletions ----
      const delStart = i; // Index in original sentence where deletion starts
      const delWords = [];
      // Find consecutive words in the original sentence that aren't in the LCS
      // These are words that have been deleted in the new text
      // lcs[i][j] === lcs[i + 1][j] means the LCS doesn't change when skipping the current word in oldWords,
      // which indicates this word isn't part of the LCS
      while (i < oldWords.length && (j >= newWords.length || lcs[i][j] === lcs[i + 1][j])) {
        delWords.push(oldWords[i]);
        i++;
      }
      // If any words were deleted, add them to the deletions array with their starting position
      if (delWords.length > 0) {
        deletions.push({ index: delStart, value: delWords });
      }

      // ---- Process insertions ----
      const insStart = i; // Index in original sentence where insertion should be placed
      const insWords = [];
      // Find consecutive words in the new sentence that aren't in the LCS
      // These are words that have been inserted in the new text
      // lcs[i][j] === lcs[i][j + 1] means the LCS doesn't change when skipping the current word in newWords,
      // which indicates this word isn't part of the LCS
      while (j < newWords.length && (i >= oldWords.length || lcs[i][j] === lcs[i][j + 1])) {
        insWords.push(newWords[j]);
        j++;
      }
      // If any words were inserted, add them to the insertions array with their target position
      if (insWords.length > 0) {
        insertions.push({ index: insStart, value: insWords });
      }
    }
  }

  // Return the complete set of word-level differences
  return { deletions, insertions };
}

/**
 * Generates a reversible hash for a given string.
 *
 * This function converts a string input into a 32-character alphanumeric hash.
 * It uses a variant of the djb2 hashing algorithm with BigInt to handle larger values,
 * which allows for better distribution and reduced collision probability.
 *
 * @param data - The input string to be hashed
 * @returns A 32-character alphanumeric hash string
 *
 * @remarks
 * While this function produces a deterministic output that can be used for
 * identifying text content, it is not cryptographically secure and should not
 * be used for security purposes.
 */
export function reversibleHash(data: string): string {
  let hash = 0n; // Use BigInt to handle larger numbers
  for (let i = 0; i < data.length; i++) {
    const char = BigInt(data.charCodeAt(i));
    hash = (hash << 5n) - hash + char;
  }
  const alphanumericHash = hash.toString(36); // Convert to base-36 (alphanumeric)
  return alphanumericHash.padStart(32, "0").slice(-32); // Ensure 32 characters long
}

/**
 * Calculates a similarity score between two paragraphs using a combination of
 * Jaccard similarity and n-gram analysis.
 *
 * @param paragraph1 - The first paragraph to compare
 * @param paragraph2 - The second paragraph to compare
 * @param nGramSize - Optional parameter to specify n-gram size (default: 2)
 * @returns A similarity score between 0 (completely different) and 1 (identical)
 */
export function calculateParagraphSimilarity(paragraph1: string, paragraph2: string, nGramSize = 2): number {
  // Handle edge cases
  if (paragraph1 === paragraph2) {
    return 1.0;
  }
  if (!paragraph1 || !paragraph2) {
    return 0.0;
  }

  // Normalize paragraphs: lowercase and remove extra whitespace
  const normalizedP1 = paragraph1.toLowerCase().trim().replace(/\s+/g, " ");
  const normalizedP2 = paragraph2.toLowerCase().trim().replace(/\s+/g, " ");

  // Split into words
  const words1 = normalizedP1.split(/\s+/);
  const words2 = normalizedP2.split(/\s+/);

  // Word-level Jaccard similarity
  const wordSet1 = new Set(words1);
  const wordSet2 = new Set(words2);

  const intersectionSize = [...wordSet1].filter((word) => wordSet2.has(word)).length;
  const unionSize = wordSet1.size + wordSet2.size - intersectionSize;

  const wordJaccardSimilarity = unionSize === 0 ? 0 : intersectionSize / unionSize;

  // Generate n-grams for both paragraphs
  const generateNGrams = (words: string[], n: number): Set<string> => {
    const ngrams = new Set<string>();
    for (let i = 0; i <= words.length - n; i++) {
      ngrams.add(words.slice(i, i + n).join(" "));
    }
    return ngrams;
  };

  const ngrams1 = generateNGrams(words1, nGramSize);
  const ngrams2 = generateNGrams(words2, nGramSize);

  // Calculate n-gram Jaccard similarity
  const ngramIntersectionSize = [...ngrams1].filter((ngram) => ngrams2.has(ngram)).length;
  const ngramUnionSize = ngrams1.size + ngrams2.size - ngramIntersectionSize;

  const ngramJaccardSimilarity = ngramUnionSize === 0 ? 0 : ngramIntersectionSize / ngramUnionSize;

  // Combined score with more weight on n-gram similarity
  return 0.4 * wordJaccardSimilarity + 0.6 * ngramJaccardSimilarity;
}

/**
 * Escapes special characters used in MS Access wildcard searches.
 * https://learn.microsoft.com/en-us/office/dev/add-ins/word/search-option-guidance#escape-special-characters
 *
 * Special characters: ` [ ] ( ) { } * ? < > ! @ `
 * @param str - The string to escape.
 * @returns The string with special wildcard characters escaped.
 */
export function escapeAccessWildcards(str: string) {
  const specialChars = ["[", "]", "(", ")", "{", "}", "*", "?", "<", ">", "!", "@"];
  const regex = new RegExp("(" + specialChars.map((c) => "\\" + c).join("|") + ")", "g");
  return str.replace(regex, (match) => "[" + match + "]");
}

/**
 * Generates an array of word n-grams from the given text, where each n-gram is a contiguous sequence of words of the specified window size.
 *
 * @param text - The input string from which to generate n-grams.
 * @param windowSize - The number of words in each n-gram. Defaults to 5.
 * @returns An array of n-gram strings, each containing `windowSize` words.
 *
 */
export function generateWordNGrams(text: string, windowSize = 5): string[] {
  // Split the input text into words using whitespace as delimiter
  const words = text.split(/\s+/);
  const ngrams: string[] = [];

  // Loop through the words array to generate n-grams of the specified window size
  for (let i = 0; i <= words.length - windowSize; i++) {
    for (let j = -NGRAM_ROLLING_WINDOW_SIZE; j < NGRAM_ROLLING_WINDOW_SIZE; j++) {
      // Create an n-gram by joining a slice of words
      const ngram = words.slice(i, Math.max(0, i + j) + windowSize).join(" ");
      ngrams.push(ngram);
    }
  }

  return ngrams;
}
